<template>
  <!-- REQ-13: Esc и подложка закрывают диалог, как «Закрыть»; пока идёт запрос заказа — нет (ответ пришёл бы в закрытый
       диалог). -->
  <v-dialog v-model="showDialog" :persistent="isLoading" content-class="client-cabinet-dialog" max-width="520px">
    <v-card data-testid="stripe-dialog">
      <v-card-title>
        <h2 class="text-h6">{{ $t("client_page.cabinet.pay_dialog.stripe_title") }}</h2>
      </v-card-title>
      <v-card-text>
        <p class="text-subtitle-1 mb-4" data-testid="stripe-dialog-amount">
          {{ $t("client_page.cabinet.pay_dialog.amount_line", { amount: amountText }) }}
        </p>

        <p v-if="errorMessage" class="client-cabinet-error mb-3" role="alert" data-testid="stripe-dialog-error">
          {{ errorMessage }}
        </p>

        <template v-if="paymentUrl">
          <p class="mb-2">{{ $t("client_page.cabinet.pay_dialog.link_hint") }}</p>
          <a :href="paymentUrl" target="_blank" rel="noopener" class="client-cabinet-link" data-testid="stripe-dialog-open-link">
            {{ $t("client_page.cabinet.pay_dialog.open_again") }}
          </a>
          <div v-if="qrCode" class="text-center mt-4">
            <img :src="qrCode" :alt="$t('client_page.cabinet.pay_dialog.qr_alt')" style="max-width: 250px; width: 100%;" />
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="flex-wrap">
        <v-btn text min-height="44" @click="closeDialog">{{ $t("client_page.cabinet.pay_dialog.close") }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!paymentUrl"
          color="primary"
          depressed
          min-height="44"
          :loading="isLoading"
          :disabled="isLoading"
          data-testid="stripe-dialog-submit"
          @click="createPayment"
        >
          {{ $t("client_page.cabinet.pay_dialog.go") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// FT-200 STEP-05 (REQ-09, REQ-11, U8): оплата Stripe из кабинета клиента.
//
// Открывается из блока «Оплата» на сумму, которую выбрал клиент («Оплатить X» или своя сумма) — prop amount; сумма
// не обнуляется. Заказ — по паре номер + фамилия, без client_id: сервер берёт клиента из бронирования. Адреса
// возврата — страница результата кабинета (clientCabinetReturn), не настройка компании. До перехода к банку во вкладку
// пишется операция — по ней страница возврата узнает итог (Task 6). Подписок на каналы нет (REQ-12): свежие данные
// кабинет перечитает при возвращении во вкладку.
// Доступность — только company.payment_methods.stripe нового ответа; старые ключи Stripe не читаются.
import QRCode from "qrcode";
import clientCabinetReturn from "@/utils/clientCabinetReturn";
import clientCabinetSession from "@/utils/clientCabinetSession";
import { openPaymentUrl } from "@/utils/tinkoffCard";
import { money, parseAmount, paymentErrorKey, reportServerError, roundMoney } from "@/utils/clientCabinetView";

export const STRIPE_ORDERS_PATH = "/api/v1/integrations/public_stripe_payments/orders";

export default {
  name: "StripePaymentDialogPublic",
  props: {
    bookingId: { type: [Number, String], required: true },
    lastName: { type: String, required: true },
    // Начальная сумма диалога: то, на что нажал клиент.
    amount: { type: [Number, String], default: null },
    company: { type: Object, required: true },
  },
  data() {
    return {
      showDialog: false,
      paymentAmount: null,
      paymentUrl: null,
      qrCode: null,
      isLoading: false,
      orderId: null,
      errorMessage: null,
    };
  },
  computed: {
    stripeAvailable() {
      const methods = this.company && this.company.payment_methods;
      return !!(methods && methods.stripe);
    },
    amountText() {
      return money(this.paymentAmount, this.company && this.company.currency_symbol);
    },
  },
  watch: {
    // Закрыт Esc или подложкой — как «Закрыть»: попытка сбрасывается.
    showDialog(open) {
      if (!open) this.resetDialog();
    },
  },
  methods: {
    open() {
      if (!this.stripeAvailable) return false;
      this.resetDialog();
      this.showDialog = true;
      return true;
    },

    closeDialog() {
      this.showDialog = false;
      this.resetDialog();
    },

    // Состояние прошлой попытки стирается, сумма возвращается к начальной.
    resetDialog() {
      const amount = parseAmount(this.amount);
      this.paymentAmount = Number.isNaN(amount) ? null : roundMoney(amount);
      this.paymentUrl = null;
      this.qrCode = null;
      this.orderId = null;
      this.errorMessage = null;
    },

    createPayment() {
      if (this.isLoading) return Promise.resolve();
      const amount = parseAmount(this.paymentAmount);
      if (!(amount > 0)) {
        this.errorMessage = this.$t("client_page.cabinet.payment.amount_invalid");
        return Promise.resolve();
      }
      if (!this.stripeAvailable) {
        this.errorMessage = this.$t("client_page.cabinet.pay_dialog.errors.unavailable");
        return Promise.resolve();
      }

      const payload = { booking_id: this.bookingId, last_name: this.lastName, amount: roundMoney(amount) };
      const urls = clientCabinetReturn.urls("payment", this.company);
      if (urls) {
        payload.success_url = urls.success_url;
        payload.cancel_url = urls.cancel_url;
      }

      this.isLoading = true;
      this.errorMessage = null;
      return this.axios
        .post(STRIPE_ORDERS_PATH, payload)
        .then((response) => {
          const data = (response && response.data) || {};
          const url = data.payment_url || data.checkout_url;
          if (!url) {
            this.errorMessage = this.$t("client_page.cabinet.pay_dialog.errors.failed");
            return;
          }
          this.orderId = data.order_id;
          clientCabinetSession.startOperation({ flow: "payment", provider: "stripe", orderId: data.order_id });
          this.paymentUrl = url;
          this.generateQrCode(url);
          openPaymentUrl(url);
        })
        .catch((error) => {
          this.errorMessage = this.$t(paymentErrorKey(error));
          reportServerError(this, "ClientCabinet: заказ Stripe", error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },

    generateQrCode(url) {
      QRCode.toDataURL(url, { width: 250, margin: 2 })
        .then((dataUrl) => {
          this.qrCode = dataUrl;
        })
        .catch(() => {
          this.qrCode = null;
        });
    },
  },
};
</script>
